import React, { memo, useEffect } from 'react';

import { LoadingOverlay, MuiThemeProvider } from '@schoolinks/ui-components';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import HttpApi from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

import { isEqual } from 'lodash';
import { shape, func } from 'prop-types';
import { CookiesProvider } from 'react-cookie';
import { initReactI18next } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import DayJsUtils from '@mui/lab/AdapterDayjs';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import CssBaseline from '@mui/material/CssBaseline';
// Ensures that CSS module styles are applied without being overwritten by MUI styles
import { StyledEngineProvider } from '@mui/material/styles';

import ErrorBox from 'components/Common/ErrorBox/ErrorBox';
import FullStory from 'components/Common/FullStory';
import Intercom from 'components/Common/Intercom';
import { isElementaryPage } from 'components/Common/LayoutV2/utils';
import UnsupportedBrowserInterrupt from 'components/Modals/UnsupportedBrowserInterrupt';
import { DEBUG_I18N, TRANSLATOR_URL } from 'config_app';
import {
  HIGH_SCHOOL_YEARS_LIST,
  MIDDLE_SCHOOL_YEARS_LIST,
} from 'constants/gradeYears';
import { LANGUAGE_STORAGE_KEY } from 'core/axios/interceptors/addLanguageInterceptor';
import useAccessibility from 'hooks/useAccessibility';
import useAppConfig from 'hooks/useAppConfig';
import useMediaInfo from 'hooks/useMediaInfo';
import useUserContext from 'hooks/useUserContext';
import createDebugTranslation from 'i18n/createDebugTranslation';
import { getSelectedLanguage } from 'i18n/utils';
import enTranslations from 'locales/en';
import ReactQueryProvider from 'providers/reactQuery';
import { UtilsProvider } from 'providers/utils';
import RoutesWrapper from 'routes';
import DragDropProvider from 'utils/DragDropProvider';

import '@schoolinks/ui-components/src/utils/variables.css';
import { SET_TTS_DATA } from 'modules/ElementaryCareers/store/actionTypes';

// Initialize i18next without blocking
i18n.use(ChainedBackend).use(LanguageDetector).use(initReactI18next);

// Non-blocking initialization
const initI18n = async () => {
  try {
    await i18n.init({
      lng: getSelectedLanguage(),
      fallbackLng: 'en',
      // Define two namespaces:
      //   1) "translation"    -> loaded from backend
      //   2) "fallback"       -> your local file
      ns: ['translation'],
      // i18n will look in "translation" first and fallback to "fallback"
      // if the key is missing in "translation"
      defaultNS: 'translation',
      fallbackNS: 'fallback',
      partialBundledLanguages: true,
      backend: {
        backends: [LocalStorageBackend, HttpApi],
        backendOptions: [
          {
            prefix: 'i18nextRes_',
            expirationTime: 4 * 60 * 60 * 1000, // 4 hours in milliseconds
          },
          {
            loadPath: `${TRANSLATOR_URL}/{{lng}}`,
            requestOptions: {
              timeout: 1000,
            },
          },
        ],
      },
      detection: {
        order: ['localStorage', 'cookie', 'navigator'],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        lookupCookie: LANGUAGE_STORAGE_KEY,
        caches: ['localStorage', 'cookie'],
        cookieExpirationDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365), // 1 year
      },
      resources: {
        en: {
          fallback: enTranslations,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      load: 'currentOnly',
    });

    if (DEBUG_I18N === 'true') {
      const originalT = i18n.t.bind(i18n);
      i18n.t = (key, options) => {
        if (options?.debugAsText) {
          return `_${originalT(key, options)}_`;
        }
        return createDebugTranslation(originalT)(key, options);
      };
    }
  } catch (error) {
    console.warn(
      'i18n initialization failed, falling back to local translations:',
      error,
    );
  }
};

// Start initialization but don't wait for it
initI18n();

/**
 * @prop {ReduxStore} store -> store object of redux
 * @prop {UserReducerObject} user -> user reducer object from redux store
 * @returns
 */
const App = memo(
  ({ store }) => {
    const { isMobile, isTablet, isUnsupported } = useMediaInfo();
    const {
      affiliations,
      isLoggedIn,
      isMember,
      isStudent,
      currentStudentGrade,
      moduleAccess,
      moduleAccessLoaded,
    } = useSelector(({ user }) => user);
    const dispatch = useDispatch();

    // enforce TTS off for middle and high school students
    useEffect(() => {
      const isMiddleSchoolStudent =
        isStudent && MIDDLE_SCHOOL_YEARS_LIST.includes(currentStudentGrade);
      const isHighSchoolStudent =
        isStudent && HIGH_SCHOOL_YEARS_LIST.includes(currentStudentGrade);

      if (isHighSchoolStudent || isMiddleSchoolStudent) {
        dispatch({
          type: SET_TTS_DATA,
          payload: {
            ttsOption: 0,
            playingItem: -1,
            ttsEnabledItems: [],
            isPlaying: false,
          },
        });
        localStorage.removeItem('read_aloud_state');
      }
    }, [isStudent, currentStudentGrade, dispatch]);

    /*
        Handles important events for users, handles them with useEffect life-cycle hook on mount, and unmount
        - Responsive support on window re-sizing
        - Giving news for users if there's a new version
     */
    useAppConfig();
    /*
        Loads the layout/context of the user depending on given logic. 
        Uses redux actions to operate these asynchronous data operations.
    */
    useUserContext({
      data: { isLoggedIn, isMember, moduleAccess, affiliations },
    });

    useAccessibility();

    if (__DEV__) window.user = store.getState().user;
    if (!moduleAccessLoaded) {
      return <LoadingOverlay />;
    }
    const hideIntercom =
      isMobile || (isElementaryPage(window.location.pathname) && isTablet);

    return (
      <ReactQueryProvider>
        <UtilsProvider>
          <LocalizationProvider dateAdapter={DayJsUtils}>
            <StyledEngineProvider injectFirst>
              <MuiThemeProvider>
                <CookiesProvider>
                  <DragDropProvider>
                    <CssBaseline />
                    <BrowserRouter>
                      <Sentry.ErrorBoundary fallback={ErrorBox}>
                        <RoutesWrapper />
                      </Sentry.ErrorBoundary>
                    </BrowserRouter>
                    {isUnsupported && <UnsupportedBrowserInterrupt />}
                    {!hideIntercom ? <Intercom /> : null}
                    <FullStory />
                  </DragDropProvider>
                </CookiesProvider>
              </MuiThemeProvider>
            </StyledEngineProvider>
          </LocalizationProvider>
        </UtilsProvider>
      </ReactQueryProvider>
    );
  },
  // We shouldn't update the component if props haven't changed
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);

App.propTypes = {
  store: shape({
    getState: func.isRequired,
  }).isRequired,
};

export default App;
